import {
  ALL_AVAILABLE_LANGUAGES,
  AvailableLanguages,
  BookType,
  DEFAULT_LOCALE,
  EntityId,
  PrinticularProductTemplateVariant,
  PrintServiceProductEntity,
  PrintServiceProductEntityHydrated,
  PrintServiceProductImageEntityHydrated,
} from "@jackfruit/common"
import {
  PageType,
  ProductPageEntity,
  ProductPageEntityHydrated,
} from "~/interfaces/entities/ProductPage"
import { getPagesFromProduct } from "~/services/PrintServiceProductHelpers"

export const photoBookHasWindowCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "windowCover"
  })

  return hasCover
}

export const photoBookHasSimpleCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "cover"
  })

  return hasCover
}

export const photoBookHasCustomCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "customCover"
  })

  return hasCover
}

export const getPhotoBookCoverInfo = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasSimpleCover = photoBookHasSimpleCover(productImages)
  const hasWindowCover = photoBookHasWindowCover(productImages)
  const hasCustomCover = photoBookHasCustomCover(productImages)

  const hasCover = hasSimpleCover || hasWindowCover || hasCustomCover
  return {
    hasCover,
    hasSimpleCover,
    hasWindowCover,
    hasCustomCover,
  }
}

export const getPhotoBookPagePosition = (
  pageIndex: number,
  productImages: PrintServiceProductImageEntityHydrated[] = []
) => {
  const { hasCover } = getPhotoBookCoverInfo(productImages)
  return {
    isLeftPage: hasCover ? pageIndex % 2 === 1 : pageIndex % 2 === 0,
    isFirstPage: hasCover ? pageIndex === 2 : pageIndex === 0,
  }
}

export const photoBookFindImageForPage = (
  productPage: ProductPageEntity,
  productImages: PrintServiceProductImageEntityHydrated[],
  productPageIds: EntityId[],
  language: AvailableLanguages
) => {
  const currentPageIndex = productPageIds.indexOf(productPage.id)
  const { hasSimpleCover, hasWindowCover, hasCustomCover } =
    getPhotoBookCoverInfo(productImages)

  let foundPageImage: PrintServiceProductImageEntityHydrated | undefined

  switch (productPage.pageType) {
    case PageType.FrontCover:
      if (hasSimpleCover) {
        foundPageImage = productImages.find(image => {
          const coverImageLocale = image?.metaData?.locale || DEFAULT_LOCALE

          if (!ALL_AVAILABLE_LANGUAGES.includes(coverImageLocale)) {
            console.warn(
              `Invalid locale ${coverImageLocale}. List locales: ${ALL_AVAILABLE_LANGUAGES}`
            )
          }

          return (
            image.metaData?.photoBook?.frameType === "cover" &&
            language === coverImageLocale
          )
        })

        // If no foundPageImage, fallback to checking frameType
        if (!foundPageImage) {
          foundPageImage = productImages.find(
            image => image.metaData?.photoBook?.frameType === "cover"
          )
        }
      }
      if (hasWindowCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "windowCover"
        })
      }
      if (hasCustomCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "customCover"
        })
      }
      break
    case PageType.FrontCoverInside:
      foundPageImage = productImages.find(image => {
        return image.metaData?.photoBook?.frameType === "insideCover"
      })
      break

    case PageType.Page:
      // 3 cases, leftPage, rightPage, windowFirstPage
      const { isFirstPage, isLeftPage: isLeft } = getPhotoBookPagePosition(
        currentPageIndex,
        productImages
      )

      if (isFirstPage && hasWindowCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "windowFirstPage"
        })
      } else if (isFirstPage && hasSimpleCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "rightPage"
        })
      } else if (isLeft) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "leftPage"
        })
      } else {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "rightPage"
        })
      }
      break
    case PageType.BackCoverInside:
      foundPageImage = productImages.find(image => {
        return image.metaData?.photoBook?.frameType === "lastPage"
      })
      break
  }

  if (!foundPageImage) {
    console.warn("Could not find image for page", productPage, currentPageIndex)
  }

  return foundPageImage
}

export const getInsideProductPages = (pages: ProductPageEntity[]) =>
  pages.filter(
    page => page.pageType === PageType.Page || !Boolean(page.pageType)
  )

export const getDefaultInnerPageSpreadForProduct = (
  product: PrintServiceProductEntityHydrated
) => {
  const requiredProductPages = getPagesFromProduct(product)
  const allInsidePages = requiredProductPages.filter(
    page => page.pageType === PageType.Page
  )

  const leftPage = allInsidePages[1]
  const rightPage = allInsidePages[2]

  return { leftPage, rightPage }
}

export const getImageCountForPage = (page: ProductPageEntityHydrated) => {
  return page.imageRegions.length
}

export const getImageCountPerPageSpreadForProduct = (
  product: PrintServiceProductEntityHydrated,
  variant?: PrinticularProductTemplateVariant,
  position?: number
) => {
  if (variant && position) {
    const { leftTemplatePage, rightTemplatePage, agnosticTemplatePages } =
      resolveInsideTemplatePages(variant)

    const leftAgnosticPage =
      agnosticTemplatePages[position % agnosticTemplatePages.length]
    const rightAgnosticPage =
      agnosticTemplatePages[(position + 1) % agnosticTemplatePages.length]

    const leftPage = leftTemplatePage ?? leftAgnosticPage
    const rightPage = rightTemplatePage ?? rightAgnosticPage

    const leftPageImageCount = leftPage?.imageRegions?.length ?? 0
    const rightPageImageCount = rightPage?.imageRegions?.length ?? 0

    return leftPageImageCount + rightPageImageCount
  }

  const { leftPage, rightPage } = getDefaultInnerPageSpreadForProduct(product)

  const leftPageImageCount = getImageCountForPage(leftPage)
  const rightPageImageCount = getImageCountForPage(rightPage)

  return leftPageImageCount + rightPageImageCount
}

export const getMinAndMaxImagesForPhotoBookProduct = (
  product: PrintServiceProductEntityHydrated
) => {
  const {
    minImages: serverExpectedMinImages,
    maxImages: serverExpectedMaxImages,
    metaData,
  } = product

  const photoBookType: BookType = metaData?.photoBook?.bookType || "duplex"
  const { printServiceProductImages = [] } = product
  const hasCustomCover = photoBookHasCustomCover(printServiceProductImages)

  const minImagesCountForPages = hasCustomCover
    ? serverExpectedMinImages - 1
    : serverExpectedMinImages

  const maxImagesCountForPages = hasCustomCover
    ? serverExpectedMaxImages - 1
    : serverExpectedMaxImages

  let minPages = minImagesCountForPages
  let maxPages = maxImagesCountForPages

  if (photoBookType === "simplex") {
    minPages *= 2
    maxPages *= 2
  }

  const result = {
    minImages: serverExpectedMinImages,
    maxImages: serverExpectedMaxImages,
    hasCustomCover,
    minPages,
    maxPages,
  }

  return result
}

export const resolveInsideTemplatePages = (
  variant: PrinticularProductTemplateVariant
) => {
  const insideTemplatePages = variant.pages.filter(
    page => page.pageType === "inside-page"
  )
  const agnosticTemplatePages = insideTemplatePages.filter(
    page => !page.pageSide
  )
  const leftTemplatePage = insideTemplatePages.find(
    page => page.pageSide === "left"
  )
  const rightTemplatePage = insideTemplatePages.find(
    page => page.pageSide === "right"
  )
  return {
    insideTemplatePages,
    agnosticTemplatePages,
    leftTemplatePage,
    rightTemplatePage,
  }
}

export const isPageButtonClickable = (page: ProductPageEntity) => {
  return page.imageRegionIds.length > 0
}

export const isProductAPhotoBook = (
  product: PrintServiceProductEntityHydrated | PrintServiceProductEntity
) => {
  return product.categoryName === "photo-book"
}
